import {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useAuth,
} from "../../auth/AuthContext";

import {
  verifyCurrentPassword,
} from "../../auth/auth.service";

import SecureConfirm from "../../components/SecureConfirm/SecureConfirm";

import {
  logAuditEvent,
} from "../../core/audit.service";

import AgendaGrid from "./components/AgendaGrid";
import BookingModal from "./components/BookingModal";
import DateNavigator from "./components/DateNavigator";
import UndoToast from "./components/UndoToast";

import {
  createClient,
  loadClients,
} from "../clientes/clientes.service";

import type {
  Client,
  ClientLevel,
} from "../clientes/types";

import {
  criarReservaQuadra,
  editarReservaQuadra,
  loadAgenda,
  loadRecurringLessons,
  pagarReservaQuadra,
  cancelarReservaQuadra,
  restaurarReservaQuadra,
} from "./quadras.service";

import type {
  AgendaState,
  Booking,
  FormaPagamentoQuadra,
} from "./types";

import "./Quadras.css";


type SelectedSlot = {
  court: string;
  hour: number;
};


type UndoState = {
  booking: Booking;
} | null;


type QuickClientInput = {
  nome: string;
  telefone: string;
  nivel: ClientLevel;
};


function todayISO() {
  const now =
    new Date();

  const year =
    now.getFullYear();

  const month =
    String(
      now.getMonth() + 1,
    ).padStart(
      2,
      "0",
    );

  const day =
    String(
      now.getDate(),
    ).padStart(
      2,
      "0",
    );

  return `${year}-${month}-${day}`;
}


function formatHour(
  hour: number,
) {
  const h =
    Math.floor(hour);

  const minutes =
    hour % 1 === 0
      ? "00"
      : "30";

  return `${String(h).padStart(
    2,
    "0",
  )}:${minutes}`;
}


function calculateBookingValue(
  duration: number,
) {
  if (duration === 0.5) {
    return 25;
  }

  if (duration === 1) {
    return 50;
  }

  if (duration === 1.5) {
    return 60;
  }

  return duration * 40;
}


function getVisibleHours(
  date: string,
) {
  const selected =
    new Date(
      `${date}T12:00:00`,
    );

  const day =
    selected.getDay();

  const hours:
    number[] = [];

  if (
    day === 0 ||
    day === 6
  ) {
    for (
      let h = 6;
      h < 8;
      h += 0.5
    ) {
      hours.push(h);
    }
  }

  for (
    let h = 16;
    h < 23;
    h += 0.5
  ) {
    hours.push(h);
  }

  return hours;
}


export default function Quadras() {
  const {
    profile,
    session,
    can,
  } =
    useAuth();


  const [
  agenda,
  setAgenda,
] =
  useState<AgendaState>({
    courts: [],
    bookings: [],
  });


const [
  recurringLessons,
  setRecurringLessons,
] =
  useState<Booking[]>(
    [],
  );


const [
  clients,
  setClients,
] =
  useState<Client[]>([]);


  const [
    selectedClient,
    setSelectedClient,
  ] =
    useState<Client | null>(
      null,
    );


  const [
    date,
    setDate,
  ] =
    useState(
      todayISO(),
    );


  const [
    loading,
    setLoading,
  ] =
    useState(true);


  const [
    saving,
    setSaving,
  ] =
    useState(false);


  const [
    receiving,
    setReceiving,
  ] =
    useState(false);


  const [
    error,
    setError,
  ] =
    useState("");


  const [
    paymentError,
    setPaymentError,
  ] =
    useState("");


  const [
    selectedSlot,
    setSelectedSlot,
  ] =
    useState<SelectedSlot | null>(
      null,
    );


  const [
    selectedBooking,
    setSelectedBooking,
  ] =
    useState<Booking | null>(
      null,
    );


  const [
    cliente,
    setCliente,
  ] =
    useState("");


  const [
    duration,
    setDuration,
  ] =
    useState(1);


  const [
    bookingValue,
    setBookingValue,
  ] =
    useState(0);


  const [
    formError,
    setFormError,
  ] =
    useState("");


  const [
    undoState,
    setUndoState,
  ] =
    useState<UndoState>(
      null,
    );


  const [
    confirmCancelOpen,
    setConfirmCancelOpen,
  ] =
    useState(false);


  useEffect(() => {
    async function load() {
      try {
        setLoading(true);

        const [
          agendaResult,
          clientsResult,
        ] =
          await Promise.all([
            loadAgenda(),
            loadClients(),
          ]);

        setAgenda(
          agendaResult,
        );

        setClients(
          clientsResult,
        );

        setError("");
      } catch (err) {
        console.error(
          err,
        );

        setError(
          "Não foi possível carregar a Agenda.",
        );
      } finally {
        setLoading(false);
      }
    }

    load();
  }, []);


/*
 * NOVO:
 * Carrega as aulas recorrentes
 * sempre que a data mudar.
 */
useEffect(() => {
  let active =
    true;


  async function loadLessons() {
    try {
      const lessons =
        await loadRecurringLessons(
          date,
        );


      if (active) {
        setRecurringLessons(
          lessons,
        );
      }
    } catch (err) {
      console.error(
        "Não foi possível carregar as aulas recorrentes:",
        err,
      );


      if (active) {
        setRecurringLessons(
          [],
        );
      }
    }
  }


  loadLessons();


  return () => {
    active =
      false;
  };
}, [date]);


  const visibleHours =
    useMemo(
      () =>
        getVisibleHours(
          date,
        ),
      [date],
    );


  const bookingsToday =
  useMemo(
    () => {
      const reservations =
        agenda.bookings.filter(
          (booking) =>
            booking.date ===
            date,
        );


      return [
        ...reservations,
        ...recurringLessons,
      ];
    },
    [
      agenda.bookings,
      recurringLessons,
      date,
    ],
  );


  function changeDay(
    delta: number,
  ) {
    const selected =
      new Date(
        `${date}T12:00:00`,
      );

    selected.setDate(
      selected.getDate() +
        delta,
    );

    setDate(
      selected
        .toISOString()
        .slice(0, 10),
    );
  }


  function openNewBooking(
    court: string,
    hour: number,
  ) {
    if (
      !can(
        "quadras.create",
      )
    ) {
      return;
    }

    setSelectedBooking(
      null,
    );

    setSelectedClient(
      null,
    );

    setSelectedSlot({
      court,
      hour,
    });

    setCliente("");

    setDuration(1);

    setBookingValue(0);

    setFormError("");

    setPaymentError("");
  }


  function openExistingBooking(
    booking: Booking,
  ) {
    setSelectedSlot(
      null,
    );

    setSelectedBooking(
      booking,
    );

    const linkedClient =
      booking.cliente_id
        ? clients.find(
            (client) =>
              client.id ===
              booking.cliente_id,
          ) || null
        : null;

    setSelectedClient(
      linkedClient,
    );

    setCliente(
      booking.cliente,
    );

    setDuration(
      booking.duration,
    );

    setBookingValue(
      Number(
        booking.price ??
        0,
      ),
    );

    setFormError("");

    setPaymentError("");
  }


  function handleClientTextChange(
    value: string,
  ) {
    setCliente(value);

    setSelectedClient(
      null,
    );
  }


  function handleDurationChange(
    nextDuration: number,
  ) {
    setDuration(
      nextDuration,
    );

    setBookingValue(
      calculateBookingValue(
        nextDuration,
      ),
    );
  }


  function handleClientSelect(
    client: Client,
  ) {
    setSelectedClient(
      client,
    );

    setCliente(
      client.nome,
    );
  }


  function closeModal() {
    if (
      saving ||
      receiving
    ) {
      return;
    }

    setSelectedSlot(
      null,
    );

    setSelectedBooking(
      null,
    );

    setSelectedClient(
      null,
    );

    setConfirmCancelOpen(
      false,
    );

    setCliente("");

    setDuration(1);

    setBookingValue(0);

    setFormError("");

    setPaymentError("");
  }


  function hasConflict(
    court: string,
    hour: number,
    bookingDuration: number,
    ignoreBookingId?: string,
  ) {
    const end =
      hour +
      bookingDuration;

    return bookingsToday.some(
      (booking) => {
        if (
          booking.court !==
          court
        ) {
          return false;
        }

        if (
          booking.id ===
          ignoreBookingId
        ) {
          return false;
        }

        const existingStart =
          booking.hour;

        const existingEnd =
          booking.hour +
          booking.duration;

        return (
          hour <
            existingEnd &&
          end >
            existingStart
        );
      },
    );
  }


  function isInsideNormalHours(
    hour: number,
    bookingDuration: number,
  ) {
    const end =
      hour +
      bookingDuration;

    if (
      hour < 12
    ) {
      return (
        hour >= 6 &&
        end <= 8
      );
    }

    return (
      hour >= 16 &&
      end <= 23
    );
  }


  async function handleQuickCreateClient(
    input: QuickClientInput,
  ) {
    if (
      !can(
        "clientes.create",
      )
    ) {
      throw new Error(
        "Você não possui permissão para cadastrar clientes.",
      );
    }

    const created =
      await createClient({
        nome:
          input.nome,

        telefone:
          input.telefone,

        nivel:
          input.nivel,
      });

    setClients(
      (current) =>
        [
          ...current,
          created,
        ].sort(
          (a, b) =>
            a.nome.localeCompare(
              b.nome,
              "pt-BR",
            ),
        ),
    );

    try {
      await logAuditEvent({
        userId:
          session?.user.id,

        employeeId:
          profile?.id,

        employeeName:
          profile?.nome,

        employeeLoginId:
          profile?.login_id,

        eventType:
          "CLIENTE_CRIADO",

        level:
          "success",

        module:
          "clientes",

        entityType:
          "cliente",

        entityId:
          created.id,

        description:
          `${profile?.nome || "Funcionário"} cadastrou rapidamente o cliente ${created.nome} durante uma reserva.`,

        metadata: {
          cliente:
            created.nome,

          telefone:
            created.telefone,

          nivel:
            created.nivel,

          origem:
            "agenda",
        },
      });
    } catch (
      auditError
    ) {
      console.error(
        "Falha na auditoria:",
        auditError,
      );
    }

    return created;
  }


  async function confirmBooking() {
    if (
      !selectedSlot
    ) {
      return;
    }

    const cleanName =
      cliente.trim();

    if (!cleanName) {
      setFormError(
        "Informe o nome do cliente.",
      );

      return;
    }

    if (
      !isInsideNormalHours(
        selectedSlot.hour,
        duration,
      )
    ) {
      setFormError(
        "A duração ultrapassa o horário normal de funcionamento.",
      );

      return;
    }

    if (
      hasConflict(
        selectedSlot.court,
        selectedSlot.hour,
        duration,
      )
    ) {
      setFormError(
        "Esse horário entra em conflito com outra reserva.",
      );

      return;
    }


    try {
      setSaving(true);

      setFormError("");

      const result =
  await criarReservaQuadra({
    court:
      selectedSlot.court,

    data:
  date,

    hora:
      selectedSlot.hour,

    duracao:
      duration,

    cliente:
      cleanName,

    clienteId:
      selectedClient?.id ??
      null,

    quantidadePessoas:
      1,
  });


const agendaAtualizada =
  await loadAgenda();


setAgenda(
  agendaAtualizada,
);


      try {
        await logAuditEvent({
          userId:
            session?.user.id,

          employeeId:
            profile?.id,

          employeeName:
            profile?.nome,

          employeeLoginId:
            profile?.login_id,

          eventType:
            "RESERVA_CRIADA",

          level:
            "success",

          module:
            "quadras",

          entityType:
            "reserva",

          entityId:
  String(
    result.reserva.id,
  ),

          description:
            `${profile?.nome || "Funcionário"} criou uma reserva para ${cleanName}.`,

          metadata: {
            cliente:
              cleanName,

            cliente_id:
  result.reserva.cliente_id ??
  null,

            quadra:
              selectedSlot.court,

            data:
              date,

            horario:
              formatHour(
                selectedSlot.hour,
              ),

            duracao:
              duration,

            valor:
  Number(
    result.reserva.valor,
  ),

            pago:
              false,
          },
        });
      } catch (
        auditError
      ) {
        console.error(
          "Falha na auditoria:",
          auditError,
        );
      }


      closeModal();
    } catch (err) {
  console.error(
    err,
  );

  let message =
    "Não foi possível salvar a reserva.";

  if (
    typeof err === "object" &&
    err !== null &&
    "message" in err
  ) {
    const errorMessage =
      String(
        (
          err as {
            message?: string;
          }
        ).message ??
        "",
      ).trim();

    if (errorMessage) {
      message =
        errorMessage;
    }
  }

  setFormError(
    message,
  );
} finally {
      setSaving(false);
    }
  }


  async function saveBookingChanges() {
    if (
      !selectedBooking
    ) {
      return;
    }

    if (
      !can(
        "quadras.edit",
      )
    ) {
      setFormError(
        "Você não possui permissão para editar reservas.",
      );

      return;
    }

    if (
      selectedBooking.pago
    ) {
      setFormError(
        "Uma reserva paga não pode ser alterada.",
      );

      return;
    }

    const cleanName =
      cliente.trim();

    if (!cleanName) {
      setFormError(
        "Informe o nome do cliente.",
      );

      return;
    }

    if (
      !Number.isFinite(
        bookingValue,
      ) ||
      bookingValue < 0
    ) {
      setFormError(
        "Informe um valor válido para a reserva.",
      );

      return;
    }

    if (
      !isInsideNormalHours(
        selectedBooking.hour,
        duration,
      )
    ) {
      setFormError(
        "A duração ultrapassa o horário normal de funcionamento.",
      );

      return;
    }

    if (
      hasConflict(
        selectedBooking.court,
        selectedBooking.hour,
        duration,
        selectedBooking.id,
      )
    ) {
      setFormError(
        "Essa alteração entra em conflito com outra reserva.",
      );

      return;
    }

    try {
      setSaving(true);

      setFormError("");

      const result =
  await editarReservaQuadra({
    reservaId:
      selectedBooking.id,

    cliente:
      cleanName,

    clienteId:
      selectedClient?.id ??
      null,

    duracao:
      duration,

    valor:
      bookingValue,

      updatedAtEsperado:
  selectedBooking.updated_at ??
  null,
  });


const agendaAtualizada =
  await loadAgenda();


setAgenda(
  agendaAtualizada,
);


      try {
        await logAuditEvent({
          userId:
            session?.user.id,

          employeeId:
            profile?.id,

          employeeName:
            profile?.nome,

          employeeLoginId:
            profile?.login_id,

          eventType:
            "RESERVA_EDITADA",

          level:
            "info",

          module:
            "quadras",

          entityType:
            "reserva",

          entityId:
            selectedBooking.id,

          description:
            `${profile?.nome || "Funcionário"} editou a reserva de ${selectedBooking.cliente}.`,

          metadata: {
            antes: {
              cliente:
                selectedBooking.cliente,

              cliente_id:
                selectedBooking.cliente_id ??
                null,

              duracao:
                selectedBooking.duration,

              valor:
                selectedBooking.price ??
                null,
            },

            depois: {
  cliente:
    result.reserva.cliente,

  cliente_id:
    result.reserva.cliente_id ??
    null,

  duracao:
    Number(
      result.reserva.duracao,
    ),

  valor:
    Number(
      result.reserva.valor,
    ),
},

            quadra:
              selectedBooking.court,

            horario:
              formatHour(
                selectedBooking.hour,
              ),

            data:
              selectedBooking.date,
          },
        });
      } catch (
        auditError
      ) {
        console.error(
          "Falha na auditoria:",
          auditError,
        );
      }


      closeModal();
    } catch (err) {
  console.error(
    err,
  );

  let message =
    "Não foi possível salvar as alterações.";

  if (
    typeof err === "object" &&
    err !== null &&
    "message" in err
  ) {
    const errorMessage =
      String(
        (
          err as {
            message?: string;
          }
        ).message ??
        "",
      ).trim();

    if (errorMessage) {
      message =
        errorMessage;
    }
  }

  setFormError(
    message,
  );
} finally {
      setSaving(false);
    }
  }


  /*
   * =======================================================
   * PAGAMENTO
   * =======================================================
   */

  async function handleReceiveBooking(
    formaPagamento:
      FormaPagamentoQuadra,
  ) {
    if (
      !selectedBooking
    ) {
      return;
    }


    if (
      selectedBooking.pago
    ) {
      setPaymentError(
        "Esta reserva já está paga.",
      );

      return;
    }


    try {
      setReceiving(true);

      setPaymentError("");


      /*
       * A RPC faz de forma transacional:
       *
       * 1. marca a reserva como paga
       * 2. salva forma de pagamento
       * 3. salva pago_em
       * 4. gera movimento de entrada no Caixa
       */

      const result =
        await pagarReservaQuadra(
          selectedBooking.id,
          formaPagamento,
        );


      const updatedBooking:
        Booking = {
          ...selectedBooking,

          price:
            Number(
              result.valor,
            ),

          pago:
            true,

          forma_pagamento:
            result.forma_pagamento,

          pago_em:
            result.reserva
              .pago_em,
        };

      setAgenda(
        (current) => ({
          ...current,

          bookings:
            current.bookings.map(
              (booking) =>
                booking.id ===
                selectedBooking.id
                  ? updatedBooking
                  : booking,
            ),
        }),
      );


      setSelectedBooking(
        updatedBooking,
      );


      /*
       * Auditoria é separada da transação
       * financeira.
       *
       * Se o audit_events falhar, o pagamento
       * continua válido.
       */

      try {
        await logAuditEvent({
          userId:
            session?.user.id,

          employeeId:
            profile?.id,

          employeeName:
            profile?.nome,

          employeeLoginId:
            profile?.login_id,

          eventType:
            "RESERVA_PAGA",

          level:
            "success",

          module:
            "quadras",

          entityType:
            "reserva",

          entityId:
            selectedBooking.id,

          description:
            `${profile?.nome || "Funcionário"} recebeu o pagamento da reserva de ${selectedBooking.cliente}.`,

          metadata: {
            cliente:
              selectedBooking.cliente,

            cliente_id:
              selectedBooking.cliente_id ??
              null,

            quadra:
              selectedBooking.court,

            data:
              selectedBooking.date,

            horario:
              formatHour(
                selectedBooking.hour,
              ),

            duracao:
              selectedBooking.duration,

            valor:
              Number(
                result.valor,
              ),

            forma_pagamento:
              result.forma_pagamento,

            movimento_caixa_id:
              result.movimento_id,

            pago_em:
              result.reserva
                .pago_em,
          },
        });
      } catch (
        auditError
      ) {
        console.error(
          "Falha ao registrar auditoria do pagamento:",
          auditError,
        );
      }
    } catch (err) {
  console.error(
    err,
  );

  let message =
    "Não foi possível confirmar o pagamento.";

  if (
    typeof err === "object" &&
    err !== null &&
    "message" in err
  ) {
    const errorMessage =
      String(
        (
          err as {
            message?: string;
          }
        ).message ?? "",
      ).trim();

    if (errorMessage) {
      message =
        errorMessage;
    }
  }

  setPaymentError(
    message,
  );
} finally {
      setReceiving(false);
    }
  }


  async function refreshSelectedReservation() {
    const updatedAgenda = await loadAgenda();
    setAgenda(updatedAgenda);

    if (!selectedBooking) return;

    const updated = updatedAgenda.bookings.find(
      (booking) => booking.id === selectedBooking.id,
    );

    if (updated) {
      setSelectedBooking(updated);
      setBookingValue(Number(updated.price ?? 0));
    }
  }


  function requestCancelBooking() {
    if (
      !selectedBooking
    ) {
      return;
    }

    if (
      selectedBooking.pago
    ) {
      setFormError(
        "Uma reserva paga não pode ser cancelada por este fluxo.",
      );

      return;
    }

    if (
      !can(
        "quadras.delete",
      )
    ) {
      setFormError(
        "Você não possui permissão para cancelar reservas.",
      );

      return;
    }

    setConfirmCancelOpen(
      true,
    );
  }


  async function confirmCancelBooking(
  password: string,
) {
  if (
    !selectedBooking ||
    !session?.user.email
  ) {
    throw new Error(
      "Não foi possível identificar o usuário atual.",
    );
  }


  /*
   * =====================================================
   * 1. CONFIRMA A SENHA
   * =====================================================
   */

  try {
    await verifyCurrentPassword(
      session.user.email,
      password,
    );
  } catch {
    try {
      await logAuditEvent({
        userId:
          session.user.id,

        employeeId:
          profile?.id,

        employeeName:
          profile?.nome,

        employeeLoginId:
          profile?.login_id,

        eventType:
          "RESERVA_CANCELAMENTO_NEGADO",

        level:
          "security",

        module:
          "quadras",

        entityType:
          "reserva",

        entityId:
          selectedBooking.id,

        description:
          `${profile?.nome || "Funcionário"} tentou cancelar uma reserva, mas a confirmação de senha falhou.`,

        metadata: {
          cliente:
            selectedBooking.cliente,

          quadra:
            selectedBooking.court,

          data:
            selectedBooking.date,

          horario:
            formatHour(
              selectedBooking.hour,
            ),
        },
      });
    } catch (
      auditError
    ) {
      console.error(
        "Falha na auditoria:",
        auditError,
      );
    }


    throw new Error(
      "Senha incorreta.",
    );
  }


  /*
   * =====================================================
   * 2. GUARDA A RESERVA PARA O DESFAZER
   * =====================================================
   */

  const bookingToRemove =
    selectedBooking;


  /*
   * =====================================================
   * 3. CANCELA NO BANCO
   * =====================================================
   */

  try {
    setSaving(
      true,
    );

    setFormError(
      "",
    );


    await cancelarReservaQuadra(
      bookingToRemove.id,
    );


    /*
     * Recarrega a Agenda diretamente
     * da fonte oficial no banco.
     */

    const agendaAtualizada =
      await loadAgenda();


    setAgenda(
      agendaAtualizada,
    );


    /*
     * Mantém a reserva removida
     * disponível por 10 segundos
     * para o recurso Desfazer.
     */

    setUndoState({
      booking:
        bookingToRemove,
    });


    /*
     * ===================================================
     * 4. AUDITORIA
     * ===================================================
     */

    try {
      await logAuditEvent({
        userId:
          session.user.id,

        employeeId:
          profile?.id,

        employeeName:
          profile?.nome,

        employeeLoginId:
          profile?.login_id,

        eventType:
          "RESERVA_CANCELADA",

        level:
          "warning",

        module:
          "quadras",

        entityType:
          "reserva",

        entityId:
          bookingToRemove.id,

        description:
          `${profile?.nome || "Funcionário"} cancelou a reserva de ${bookingToRemove.cliente}.`,

        metadata: {
          cliente:
            bookingToRemove.cliente,

          cliente_id:
            bookingToRemove.cliente_id ??
            null,

          quadra:
            bookingToRemove.court,

          data:
            bookingToRemove.date,

          horario:
            formatHour(
              bookingToRemove.hour,
            ),

          duracao:
            bookingToRemove.duration,

          valor:
            bookingToRemove.price ??
            null,
        },
      });
    } catch (
      auditError
    ) {
      console.error(
        "Falha na auditoria:",
        auditError,
      );
    }


    setConfirmCancelOpen(
      false,
    );


    closeModal();


    window.setTimeout(
      () =>
        setUndoState(
          null,
        ),
      10000,
    );
  } catch (err) {
    console.error(
      err,
    );


    let message =
      "Não foi possível cancelar a reserva.";


    if (
      typeof err === "object" &&
      err !== null &&
      "message" in err
    ) {
      const errorMessage =
        String(
          (
            err as {
              message?: string;
            }
          ).message ??
          "",
        ).trim();


      if (errorMessage) {
        message =
          errorMessage;
      }
    }


    throw new Error(
      message,
    );
  } finally {
    setSaving(
      false,
    );
  }
}


  async function undoCancel() {
  if (!undoState) {
    return;
  }

  const restored =
    undoState.booking;

  try {
    await restaurarReservaQuadra(
      restored,
    );

    const agendaAtualizada =
      await loadAgenda();

    setAgenda(
      agendaAtualizada,
    );

    try {
      await logAuditEvent({
        userId:
          session?.user.id,

        employeeId:
          profile?.id,

        employeeName:
          profile?.nome,

        employeeLoginId:
          profile?.login_id,

        eventType:
          "RESERVA_RESTAURADA",

        level:
          "success",

        module:
          "quadras",

        entityType:
          "reserva",

        entityId:
          restored.id,

        description:
          `${profile?.nome || "Funcionário"} restaurou uma reserva cancelada.`,

        metadata: {
          cliente:
            restored.cliente,

          cliente_id:
            restored.cliente_id ??
            null,

          quadra:
            restored.court,

          data:
            restored.date,

          horario:
            formatHour(
              restored.hour,
            ),

          valor:
            restored.price ??
            null,
        },
      });
    } catch (
      auditError
    ) {
      console.error(
        "Falha na auditoria:",
        auditError,
      );
    }

    setUndoState(
      null,
    );
  } catch (err) {
    console.error(
      err,
    );

    let message =
      "Não foi possível restaurar a reserva.";

    if (
      typeof err === "object" &&
      err !== null &&
      "message" in err
    ) {
      const errorMessage =
        String(
          (
            err as {
              message?: string;
            }
          ).message ??
          "",
        ).trim();

      if (errorMessage) {
        message =
          errorMessage;
      }
    }

    setFormError(
      message,
    );
  }
}


  if (loading) {
    return (
      <section className="quadras-page">
        <div className="quadras-empty">
          Carregando Agenda...
        </div>
      </section>
    );
  }


  if (error) {
    return (
      <section className="quadras-page">
        <div className="quadras-empty">
          {error}
        </div>
      </section>
    );
  }


  const modalCourt =
    selectedBooking
      ? selectedBooking.court
      : selectedSlot?.court ||
        "";


  const modalHour =
    selectedBooking
      ? selectedBooking.hour
      : selectedSlot?.hour ||
        0;


  return (
    <section className="quadras-page">
      <div className="quadras-header">
        <div>
          <span className="quadras-eyebrow">
            AGENDA
          </span>

          <h2>
            Quadras
          </h2>

          <p>
            Visualize reservas
            e horários
            disponíveis.
          </p>
        </div>

      </div>


      <DateNavigator
        date={
          date
        }
        onDateChange={
          setDate
        }
        onPrevious={() =>
          changeDay(-1)
        }
        onNext={() =>
          changeDay(1)
        }
        onToday={() =>
          setDate(
            todayISO(),
          )
        }
      />


      <div className="agenda-wrapper">
        <AgendaGrid
          agenda={
            agenda
          }
          visibleHours={
            visibleHours
          }
          bookingsToday={
            bookingsToday
          }
          canCreate={
            can(
              "quadras.create",
            )
          }
          formatHour={
            formatHour
          }
          onFreeSlotClick={
            openNewBooking
          }
          onBookingClick={
            openExistingBooking
          }
        />
      </div>


      <BookingModal
        open={
          Boolean(
            selectedSlot ||
            selectedBooking,
          )
        }
        booking={
          selectedBooking
        }
        court={
          modalCourt
        }
        hour={
          modalHour
        }
        cliente={
          cliente
        }
        selectedClient={
          selectedClient
        }
        clients={
          clients
        }
        duration={
          duration
        }
        editableValue={
          bookingValue
        }
        saving={
          saving
        }
        error={
          formError
        }
        canDelete={
          can(
            "quadras.delete",
          )
        }
        formatHour={
          formatHour
        }
        onClienteChange={
          handleClientTextChange
        }
        onClientSelect={
          handleClientSelect
        }
        onDurationChange={
          handleDurationChange
        }
        onBookingValueChange={
          setBookingValue
        }
        onQuickCreateClient={
          handleQuickCreateClient
        }
        onClose={
          closeModal
        }
        onSave={
          selectedBooking
            ? saveBookingChanges
            : confirmBooking
        }
        onDelete={
          requestCancelBooking
        }

        /*
         * PAGAMENTO
         *
         * Por enquanto usamos quadras.edit
         * como permissão operacional para
         * receber reservas.
         *
         * Depois podemos criar uma
         * quadras.receive específica.
         */
        canReceive={
          can(
            "quadras.edit",
          )
        }
        receiving={
          receiving
        }
        paymentError={
          paymentError
        }
        onReceive={
          handleReceiveBooking
        }
        canCorrectPayments={
          profile?.cargo === "administrador"
        }
        onPaymentsChanged={
          refreshSelectedReservation
        }
      />


      <SecureConfirm
        open={
          confirmCancelOpen
        }
        title="Cancelar reserva"
        message={
          selectedBooking
            ? `Você está prestes a cancelar a reserva de ${selectedBooking.cliente}. Digite sua senha para confirmar esta ação.`
            : ""
        }
        confirmLabel="Confirmar cancelamento"
        danger
        loading={
          saving
        }
        onCancel={() =>
          setConfirmCancelOpen(
            false,
          )
        }
        onConfirm={
          confirmCancelBooking
        }
      />


      <UndoToast
        visible={
          Boolean(
            undoState,
          )
        }
        message="Reserva cancelada."
        onUndo={
          undoCancel
        }
      />
    </section>
  );
}
